import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, Search, Award, ExternalLink, GraduationCap, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CertRow {
  id: string;
  user_id: string;
  course_id: string;
  verification_code: string;
  issued_at: string;
}
interface ProfileRow {
  user_id: string;
  full_name: string | null;
  company: string | null;
  job_title: string | null;
  linkedin_url: string | null;
}
interface CourseRow {
  id: string;
  title: string;
  description: string | null;
  duration_hours: number;
  difficulty_level: string;
}

export default function Certifications() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState<CertRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileRow>>({});
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [search, setSearch] = useState("");
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [verifyInput, setVerifyInput] = useState("");

  useEffect(() => {
    (async () => {
      const [{ data: c }, { data: co }] = await Promise.all([
        supabase.from("certificates").select("*").order("issued_at", { ascending: false }).limit(500),
        supabase.from("courses").select("id,title,description,duration_hours,difficulty_level"),
      ]);
      const certList = (c ?? []) as CertRow[];
      setCerts(certList);
      setCourses((co ?? []) as CourseRow[]);

      const userIds = [...new Set(certList.map((x) => x.user_id))];
      if (userIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id,full_name,company,job_title,linkedin_url")
          .in("user_id", userIds);
        const map: Record<string, ProfileRow> = {};
        (profs ?? []).forEach((p: any) => (map[p.user_id] = p));
        setProfiles(map);
      }
      setLoading(false);
    })();
  }, []);

  const courseMap = useMemo(() => Object.fromEntries(courses.map((c) => [c.id, c])), [courses]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return certs.filter((c) => {
      if (courseFilter !== "all" && c.course_id !== courseFilter) return false;
      if (!q) return true;
      const p = profiles[c.user_id];
      return (
        p?.full_name?.toLowerCase().includes(q) ||
        p?.company?.toLowerCase().includes(q) ||
        p?.job_title?.toLowerCase().includes(q) ||
        c.verification_code.toLowerCase().includes(q)
      );
    });
  }, [certs, profiles, search, courseFilter]);

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const code = verifyInput.trim();
    if (code) navigate(`/badge/${code}`);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "QuantumBox AI Certified Professionals",
    numberOfItems: certs.length,
    itemListElement: filtered.slice(0, 50).map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://quantumleap-digital.lovable.app/badge/${c.verification_code}`,
      name: profiles[c.user_id]?.full_name || "Certified Professional",
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Certified Professionals — QuantumBox AI Academy</title>
        <meta
          name="description"
          content="Browse and verify QuantumBox AI certified professionals. Public directory of AI, prompt engineering, and automation credentials."
        />
        <link rel="canonical" href="https://quantumleap-digital.lovable.app/certifications" />
        <meta property="og:title" content="QuantumBox AI Certified Professionals" />
        <meta property="og:description" content="Verify and browse holders of QuantumBox AI credentials." />
        <meta property="og:url" content="https://quantumleap-digital.lovable.app/certifications" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <section className="pt-24 pb-12 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Shield className="h-4 w-4" /> Verified Credentials
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            QuantumBox AI Certified Professionals
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            A public directory of professionals who have completed and passed QuantumBox AI Academy
            certifications. Verify any badge below.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto">
            <Stat value={certs.length} label="Certified" icon={<Award className="h-5 w-5" />} />
            <Stat value={courses.length} label="Courses" icon={<GraduationCap className="h-5 w-5" />} />
            <Stat value="80%" label="Pass mark" icon={<CheckCircle2 className="h-5 w-5" />} />
          </div>
        </div>
      </section>

      <section className="py-8 border-y bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <form onSubmit={handleVerify} className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Paste a verification code (e.g. QBA-XXXX-XXXX)"
              value={verifyInput}
              onChange={(e) => setVerifyInput(e.target.value)}
              className="flex-1"
            />
            <Button type="submit">
              <Shield className="h-4 w-4 mr-2" /> Verify Badge
            </Button>
          </form>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, company, or job title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={courseFilter} onValueChange={setCourseFilter}>
              <SelectTrigger className="md:w-72">
                <SelectValue placeholder="All courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All courses</SelectItem>
                {courses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-16 text-muted-foreground">Loading certifications...</div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center text-muted-foreground">
                No certifications match your search.
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((c) => {
                const p = profiles[c.user_id];
                const course = courseMap[c.course_id];
                const name = p?.full_name || "Certified Professional";
                const initials = name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <Link key={c.id} to={`/badge/${c.verification_code}`} className="group">
                    <Card className="h-full hover:shadow-lg hover:border-primary/40 transition-all">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/60 text-primary-foreground flex items-center justify-center font-bold shrink-0">
                            {initials}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold truncate group-hover:text-primary">{name}</h3>
                            {p?.job_title && (
                              <p className="text-xs text-muted-foreground truncate">
                                {p.job_title}{p.company ? ` · ${p.company}` : ""}
                              </p>
                            )}
                          </div>
                          <Shield className="h-5 w-5 text-green-600 shrink-0" />
                        </div>
                        <Badge variant="secondary" className="mb-2 text-xs">
                          {course?.title || "Certification"}
                        </Badge>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{new Date(c.issued_at).toLocaleDateString()}</span>
                          <code className="font-mono">{c.verification_code}</code>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-muted/30 border-t">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-2">Available Certifications</h2>
          <p className="text-center text-muted-foreground mb-10">
            Earn industry-recognized credentials from QuantumBox AI Academy.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {courses.map((c) => (
              <Card key={c.id} className="flex flex-col">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    <Badge variant="outline" className="text-xs capitalize">{c.difficulty_level}</Badge>
                    <Badge variant="outline" className="text-xs">{c.duration_hours}h</Badge>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 flex-1 line-clamp-3">
                    {c.description}
                  </p>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/academy">Get certified <ExternalLink className="h-3 w-3 ml-2" /></Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ value, label, icon }: { value: string | number; label: string; icon: React.ReactNode }) {
  return (
    <div className="bg-card border rounded-lg p-4">
      <div className="flex items-center justify-center text-primary mb-1">{icon}</div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
